<template>
  <div class="pageWrapper" ref="pageWrapper">
    <mobile-menu :menu-items="computedMenuItems" menu-name="category" />
    <header-search mobile class="headerSearch--mobile" />

    <InfoHeader :class="[{ 'infoHeader--showLoader': showLoader }]" ref="infoHeader" />
    <PageHeader
      :class="[
        [headerPrefix ? `header--${headerPrefix}` : ''],
        { 'pageHeader--showLoader': showLoader },
      ]"
      ref="header"
      :other-accounts="otherAccounts"
      :current-company="currentCompany"
      @switch-account="checkAccountSwitch"
    />
    <mobile-menu
      :menu-items="computedUserMenuData"
      menu-name="user"
      :other-accounts="otherAccounts"
      :current-company="currentCompany"
      @switch-account="checkAccountSwitch"
    />
    <div class="pageWrapper__inner">
      <div class="hamburgerMenuWrapper">
        <hamburger-menu bordered menu-name="user" text="Menu konta" />
      </div>
      <div class="page container container--noPaddingMobile" data-sticky-container>
        <navigation
          :menu-items="computedUserMenuData"
          class="navigation sticky"
          data-margin-top="125"
        />
        <nuxt class="main" ref="page" />
      </div>
    </div>

    <main-footer />
    <modal />
  </div>
</template>

<script>
import Sticky from 'sticky-js';
import { mapState, mapActions } from 'vuex';
import MainFooter from '~/containers/MainFooter';
import MobileMenu from '~/components/MobileMenu';
import HeaderSearch from '~/components/HeaderSearch';
import Navigation from '~/components/Navigation';
import HamburgerMenu from '~/components/HamburgerMenu';
import Modal from '~/components/Modal/ModalStack';
import InfoHeader from '@/components/InfoHeader';
import PageHeader from '@/components/PageHeader';

export default {
  name: 'User',
  components: {
    HeaderSearch,
    MobileMenu,
    MainFooter,
    Navigation,
    HamburgerMenu,
    Modal,
    InfoHeader,
    PageHeader,
  },
  middleware: 'authenticated',
  data() {
    return {
      sticky: null,
    };
  },
  computed: {
    ...mapState('layout', [
      'userMenuData',
      'headerPrefix',
      'isScrollDownMobileHide',
      'mobileCategoryMenuData',
      'showLoader',
    ]),
    ...mapState('auth', ['countOrderForAcceptance']),
    ...mapState('auth', ['otherAccounts', 'acl', 'currentCompany']),
    computedUserMenuData() {
      return this.userMenuData.map(item => ({
        ...item,
        count: item.slug === 'dashboard-pendingOrders' ? this.countOrderForAcceptance : null,
        slug: item.to ? item.to : item.href ? item.href : this.localePath({ name: item.slug }),
        ...(item.altSlug && {
          altSlug: (() => {
            const localeString = this.localePath({ name: item.altSlug, params: { slug: 'x' } });
            return localeString.substring(0, localeString.length - 1);
          })(),
        }),
      }));
    },
    computedMenuItems() {
      const mapCategorySlug = category => {
        return {
          ...category,
          slug: this.localePath({ name: 'category-slug', params: { slug: category.slug } }),
          children: category.children ? category.children.map(item => mapCategorySlug(item)) : [],
        };
      };

      return this.mobileCategoryMenuData.map(item => mapCategorySlug(item));
    },
  },
  watch: {
    showLoader(val) {
      const pageHeader = this.$refs.header.$el;
      if (val) {
        const infoHeader = this.$refs.infoHeader.$el;
        if (document.documentElement.scrollTop < infoHeader.clientHeight) {
          pageHeader.style.top = `${infoHeader.clientHeight + 1}px`;
        } else {
          pageHeader.style.top = '0px';
        }
      } else {
        pageHeader.style.top = '';
      }
    },
  },
  mounted() {
    this.$auth.setAxiosHeader(this.$cookies.get('auth'));

    if (this.acl && this.acl.canSwitchToOtherAccounts) {
      this.fetchOtherAccounts();
    }
    this.getShoppingLists();
    this.fetchAutocompleteCategoryMenuData();
    this.fetchMobileCategoryMenuData();
    this.sticky = new Sticky('.sticky');
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleScroll);
    this.handleScroll();
    this.fetchCountOrderForAcceptance();
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  },
  methods: {
    ...mapActions({
      getShoppingLists: 'list/getShoppingLists',
      fetchMobileCategoryMenuData: 'layout/fetchMobileCategoryMenuData',
      fetchAutocompleteCategoryMenuData: 'layout/fetchAutocompleteCategoryMenuData',
      fetchCountOrderForAcceptance: 'auth/fetchCountOrderForAcceptance',
      fetchOtherAccounts: 'auth/fetchOtherAccounts',
      checkAccountSwitch: 'auth/checkAccountSwitch',
    }),
    handleScroll() {
      if (!this.$refs.infoHeader || !this.$refs.header) return;
      const minScroll = this.$refs.infoHeader.$el.offsetHeight;
      if (document.documentElement.scrollTop > minScroll - 1) {
        this.$refs.header.$el.addClass('pageHeader--fixed');
        this.$refs.pageWrapper.addClass('pageWrapper--fixed');
      } else {
        this.$refs.header.$el.removeClass('pageHeader--fixed');
        this.$refs.pageWrapper.removeClass('pageWrapper--fixed');
      }

      if (this.isScrollDownMobileHide && window.innerWidth <= 768) {
        this.bringMenu();
      }
    },
    bringMenu() {
      const currentScrollPos = window.pageYOffset;
      if (currentScrollPos < 130) {
        this.$refs.header.$el.removeClass('pageHeader--mobileHide');
      } else if (this.prevScrollPos > currentScrollPos) {
        this.$refs.header.$el.removeClass('pageHeader--mobileHide');
      } else {
        this.$refs.header.$el.addClass('pageHeader--mobileHide');
      }
      this.prevScrollPos = currentScrollPos;
    },
  },
};
</script>

<style lang="scss" scoped>
.page {
  display: block;
  align-items: flex-start;
  justify-content: flex-end;
  padding-top: 4rem;

  @include rwd('tablet') {
    padding-top: 1rem;
  }

  @include rwd('large-laptop', true) {
    display: flex;
  }
}

.navigation {
  display: none;

  @include rwd('large-laptop', true) {
    display: block;
    flex: 0 0 26rem;
    max-width: 26rem;
  }
}

.main {
  margin-left: 0;
  max-width: 100%;
  flex: 1;
  align-self: stretch;
  display: flex;
  flex-direction: column;

  @include rwd('large-laptop', true) {
    flex: 0 0 calc(100% - 290px);
    max-width: calc(100% - 290px);
    margin-left: 30px;
  }
}

.hamburgerMenuWrapper {
  display: none;
  padding: 1.3rem;
  margin-bottom: 3rem;
  justify-content: center;
  background-color: $cWhite;

  @include rwd('large-laptop') {
    display: flex;
  }
}
</style>
